import { useContext, useMemo } from 'react'
import { Image } from 'src/components/ui/Image'
import { formatterPrice } from 'src/utils/formatterPrice'

import { RecommendationContext } from '../../../contexts/Recommendations/recommendations-context'
import type { DataRecommendation } from '../../../types/linx-recommendations'
import type { Product } from '../../../types/product'
import { CrossSellCardLink } from './CrossSellCardLink'
import { CrossSellCardSkeleton } from './CrossSellCardSkeleton'
import { useCrossSellCardContext } from './cross-sell-card-context'

interface CheckboxProps {
  id: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

function Checkbox({
  id,
  checked,
  onChange,
  disabled,
  'aria-label': ariaLabel,
}: CheckboxProps & { 'aria-label'?: string }) {
  return (
    <div className="relative inline-flex h-6 w-6 items-center justify-center">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
        className="peer absolute h-full w-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10"
      />

      <div
        className={`
          flex h-6 w-6 items-center justify-center rounded-[2px] border-2 transition-all duration-200
          border-[#B3BAC3] bg-white
          peer-checked:bg-primaryBlue peer-checked:border-primaryBlue
          peer-hover:bg-neutral04 peer-checked:peer-hover:bg-primaryBlue
          peer-hover:ring-[6px] peer-hover:ring-neutral04 peer-checked:peer-hover:ring-[#7A83D1]
          peer-disabled:opacity-50 peer-disabled:ring-0
          peer-disabled:bg-[#F2F4F5]
        `}
      >
        <svg
          className={`h-3.5 w-3.5 text-white transition-opacity ${
            checked ? 'opacity-100' : 'opacity-0'
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={4}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
    </div>
  )
}

export function CrossSellCard() {
  const { includeCrossSellProductToCart, handleToggle } =
    useCrossSellCardContext()

  const { loading, dyDataRecommendations } = useContext(RecommendationContext)
  const areaBonusRecomendation: DataRecommendation | null = useMemo(() => {
    return dyDataRecommendations?.find(
      (item: DataRecommendation) => item.position === 'area-bonus'
    )
  }, [dyDataRecommendations])

  const showcase = areaBonusRecomendation?.showcases[0]
  const suggestionProduct = showcase?.displays?.[0]
    ?.recommendations?.[0] as Product

  const firstAvailable = suggestionProduct?.items?.find(
    (item) => item.isAvailable
  )

  const currentImage = firstAvailable?.images[0]

  const price = firstAvailable?.sellerDefault?.sellerOffer?.cashPrice ?? 0

  const listPrice = firstAvailable?.sellerDefault?.sellerOffer?.listPrice ?? 0

  const hasDiscount = listPrice > price

  const discountPercentage =
    firstAvailable?.sellerDefault?.sellerOffer?.discountPercentage ?? 0

  const handleToggleCheckbox = (checked: boolean) => {
    handleToggle(checked, suggestionProduct)
  }

  if (loading) {
    return <CrossSellCardSkeleton />
  }

  if (!areaBonusRecomendation || !suggestionProduct || !firstAvailable) {
    return null
  }

  return (
    <div className="w-full mt-8">
      <p className="mb-2 font-semibold text-base text-restructure-primary">
        Aproveite e leve junto
      </p>
      <div className="w-full flex gap-3 border border-[#DBDBDB] rounded-2xl py-[18px] px-[30px]">
        <div className="max-w-[66px] w-full mix-blend-multiply bg-[#F5F5F5]">
          <Image
            src={currentImage?.imageUrl ?? ''}
            alt={currentImage?.imageText ?? suggestionProduct.productName}
            className="w-full h-full object-cover  mix-blend-multiply bg-[#F5F5F5]"
            width={66}
            height={66}
          />
        </div>
        <div className="w-full flex items-center gap-4 justify-between grow">
          <div className="flex flex-col gap-3">
            <CrossSellCardLink product={suggestionProduct} sku={firstAvailable}>
              <p className="text-xs font-medium text-neutral10 line-clamp-2">
                {suggestionProduct.productName}
              </p>
            </CrossSellCardLink>

            <span className="flex items-center gap-1 font-bold text-neutral10 text-base">
              {formatterPrice(price)}
              {''}
              {hasDiscount && discountPercentage > 0 && (
                <span className="text-[10px] text-redDiscount font-semibold">
                  {discountPercentage}% OFF
                </span>
              )}
            </span>
          </div>
          <div>
            <Checkbox
              id="buy-together-item"
              aria-label="Incluir produto"
              checked={!!includeCrossSellProductToCart}
              onChange={handleToggleCheckbox}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
